import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { NotificationIdentifierModel } from '../models/low-level/notification-identifier.model';
import { RecipientModel } from '../models/low-level/recipient.model';

/**
 * Public contract of the recipients service: per-notification recipient lists (CRM
 * email/fax entries) saved outside the notification/confirmation payload itself. Unlike
 * some other migrated interfaces, this does not extend `InitializableInterface` — the
 * legacy service has no `isInitialized` flag.
 *
 * Implemented by both the legacy `LocalStorageRecipientsService` and the NgRx-backed
 * `RecipientsNgrxService`; consumers depend on this interface via
 * {@link RecipientsInstanceToken} rather than on a concrete class. The concrete provider
 * (legacy vs. NgRx) is chosen per app module via the `enableNgRx` environment flag.
 *
 * Method names are kept verbatim from the legacy service so no call site needed to change
 * beyond the injected type. `saveRecipientsByNotificationId` and
 * `removeRecipientsFromLocalStorage` return `Observable<unknown>` rather than the legacy
 * `Observable<SavedRecipientsInterface[]>`: no consumer reads the emitted value (all call
 * sites use it only as a completion signal), and the legacy return type is assignable to
 * this wider one, so `LocalStorageRecipientsService` still satisfies the interface unchanged.
 */
export interface RecipientsInterface {
    /** Load any previously saved recipients from storage. Swallows read failures, matching the legacy service. */
    init(): Observable<void>;

    /**
     * The recipients previously saved for the given notification, or `undefined` when
     * none have been saved.
     */
    getRecipientsByNotificationId(id: NotificationIdentifierModel): RecipientModel[] | undefined;

    /** Save the given recipients under the given notification. */
    saveRecipientsByNotificationId(recipients: RecipientModel[], id: NotificationIdentifierModel): Observable<unknown>;

    /** Remove any recipients saved for the given notification. */
    removeRecipientsFromLocalStorage(id: NotificationIdentifierModel): Observable<unknown>;
}

/**
 * DI token consumers use to resolve a {@link RecipientsInterface} implementation. The
 * concrete provider (legacy vs. NgRx) is chosen per app module via the `enableNgRx`
 * environment flag.
 */
export const RecipientsInstanceToken = new InjectionToken<RecipientsInterface>('RecipientsInterface');
